import { type CompanyUpdateInput, companyUpdateSchema } from '@app/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../../auth/store';
import { DemoPrefillButton } from '../../demo/components/DemoPrefillButton';
import { useCompany, useUpdateCompany } from '../hooks/useCompany';

function FieldSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-slate-200/90 bg-white/70 px-5 py-6 shadow-sm sm:px-6">
      <div>
        <h3 className="text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {title}
        </h3>
        {description && (
          <p className="mt-2 text-[0.88rem] leading-relaxed text-slate-600">{description}</p>
        )}
      </div>
      <div className="mt-5 space-y-5">{children}</div>
    </section>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <div>
      <span className="app-label">{label}</span>
      {children}
      {error && <p className="text-error mt-1.5">{error}</p>}
    </div>
  );
}

export function CompanyProfilePage() {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin' || role === 'super_admin';

  const { data: company, isLoading, error: loadError } = useCompany();
  const update = useUpdateCompany();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<CompanyUpdateInput>({
    resolver: zodResolver(companyUpdateSchema),
  });

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        tradeName: company.tradeName ?? '',
        legalForm: company.legalForm ?? '',
        registrationNumber: company.registrationNumber ?? '',
        taxId: company.taxId ?? '',
        countryCode: company.countryCode ?? '',
        currencyCode: company.currencyCode ?? '',
        languageCode: company.languageCode ?? '',
        timeZone: company.timeZone ?? '',
        addressLine1: company.addressLine1 ?? '',
        addressLine2: company.addressLine2 ?? '',
        postalCode: company.postalCode ?? '',
        city: company.city ?? '',
        country: company.country ?? '',
        phone: company.phone ?? '',
        email: company.email ?? '',
        website: company.website ?? '',
        industry: company.industry ?? '',
        numberOfEmployees: company.numberOfEmployees ?? undefined,
        foundingYear: company.foundingYear ?? undefined,
      });
    }
  }, [company, reset]);

  if (!isAdmin) return <Navigate to="/" />;

  if (isLoading)
    return (
      <div className="space-y-4">
        {(
          ['company-sk-1', 'company-sk-2', 'company-sk-3', 'company-sk-4', 'company-sk-5'] as const
        ).map((key) => (
          <div key={key} className="h-10 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
    );

  if (loadError)
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-5 text-red-900">
        Impossible de charger les informations de l'entreprise.
      </div>
    );

  return (
    <div className="space-y-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-xl">
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-emerald-800/95">
            Mon entreprise
          </p>
          <h2 className="mt-3 text-[2.05rem] font-semibold tracking-tight text-slate-950 sm:text-[2.2rem] sm:leading-tight">
            Profil entreprise
          </h2>
          <p className="mt-3 text-[0.9rem] leading-relaxed text-slate-600">
            Gérez les informations légales et logistiques de votre organisation.
          </p>
        </div>
        <DemoPrefillButton
          context="company-update"
          onPrefill={(data) => {
            for (const [k, v] of Object.entries(data)) {
              setValue(k as keyof CompanyUpdateInput, v as never, { shouldDirty: true });
            }
          }}
        />
      </div>

      <form
        className="space-y-6"
        noValidate
        onSubmit={handleSubmit(async (data) => {
          await update.mutateAsync(data);
        })}
      >
        {update.isSuccess && !isDirty && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            Modifications enregistrées.
          </div>
        )}
        {update.isError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
            Erreur lors de la sauvegarde.
          </div>
        )}

        <FieldSection
          title="Identification légale"
          description="Dénominations et identifiants administratifs."
        >
          <div>
            <label htmlFor="name" className="app-label">
              Raison sociale *
            </label>
            <input id="name" className="app-input" {...register('name')} />
            {errors.name && <p className="text-error mt-1.5">{errors.name.message}</p>}
          </div>
          <div>
            <label htmlFor="tradeName" className="app-label">
              Nom commercial
            </label>
            <input id="tradeName" className="app-input" {...register('tradeName')} />
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label htmlFor="legalForm" className="app-label">
                Forme juridique
              </label>
              <input id="legalForm" className="app-input" {...register('legalForm')} />
            </div>
            <div>
              <label htmlFor="registrationNumber" className="app-label">
                SIRET
              </label>
              <input
                id="registrationNumber"
                className="app-input"
                placeholder="14 chiffres"
                {...register('registrationNumber')}
              />
              {errors.registrationNumber && (
                <p className="text-error mt-1.5">{errors.registrationNumber.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="taxId" className="app-label">
                Numéro de TVA
              </label>
              <input
                id="taxId"
                className="app-input"
                placeholder="FR + 11 caractères"
                {...register('taxId')}
              />
              {errors.taxId && <p className="text-error mt-1.5">{errors.taxId.message}</p>}
            </div>
            <div>
              <label htmlFor="industry" className="app-label">
                Code APE (NAF)
              </label>
              <input
                id="industry"
                className="app-input"
                placeholder="ex. 62.01Z"
                maxLength={100}
                {...register('industry')}
              />
            </div>
          </div>
        </FieldSection>

        <FieldSection
          title="Adresse et localisation"
          description="Siège ou adresse principale utilisée comme référence."
        >
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label htmlFor="addressLine1" className="app-label">
                Adresse
              </label>
              <input id="addressLine1" className="app-input" {...register('addressLine1')} />
            </div>
            <div>
              <label htmlFor="addressLine2" className="app-label">
                Complément
              </label>
              <input id="addressLine2" className="app-input" {...register('addressLine2')} />
            </div>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            <div>
              <label htmlFor="postalCode" className="app-label">
                Code postal
              </label>
              <input id="postalCode" className="app-input" {...register('postalCode')} />
            </div>
            <div>
              <label htmlFor="city" className="app-label">
                Ville
              </label>
              <input id="city" className="app-input" {...register('city')} />
            </div>
            <div>
              <label htmlFor="country" className="app-label">
                Pays
              </label>
              <input id="country" className="app-input" {...register('country')} />
            </div>
          </div>
        </FieldSection>

        <FieldSection title="Contact" description="Coordonnées générales de l'organisation.">
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Téléphone" error={errors.phone?.message}>
              <input id="phone" type="tel" className="app-input" {...register('phone')} />
            </Field>
            <Field label="E-mail" error={errors.email?.message}>
              <input id="email" type="email" className="app-input" {...register('email')} />
            </Field>
          </div>
        </FieldSection>

        <FieldSection
          title="Présence en ligne et taille"
          description="Site institutionnel et effectifs de l'organisation."
        >
          <div>
            <label htmlFor="website" className="app-label">
              Site web
            </label>
            <input
              id="website"
              className="app-input"
              placeholder="https://…"
              {...register('website')}
            />
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Nombre d'employés" error={errors.numberOfEmployees?.message}>
              <input
                id="numberOfEmployees"
                type="number"
                min={0}
                className="app-input"
                {...register('numberOfEmployees', { valueAsNumber: true })}
              />
            </Field>
            <Field label="Année de création" error={errors.foundingYear?.message}>
              <input
                id="foundingYear"
                type="number"
                min={1800}
                max={2100}
                className="app-input"
                {...register('foundingYear', { valueAsNumber: true })}
              />
            </Field>
          </div>
        </FieldSection>

        <div className="flex justify-end">
          <button type="submit" className="btn-primary" disabled={isSubmitting || !isDirty}>
            {isSubmitting ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </button>
        </div>
      </form>
    </div>
  );
}
